/* eslint-disable react/display-name */
/* eslint-disable max-len */
import { VoidCallback1 } from '@deqo/foundation';
import { DndContext, DragEndEvent, MouseSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors } from '@dnd-kit/core';
import React, { useCallback, useMemo, useState } from 'react';
import AutoSizeInput from 'react-input-autosize';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { Button, Colors, Float, Prompt } from '.';
import { promptName } from '../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../Legacy';
import { PopperInner, PopperMount } from '../app/canvas/ui/PopperElements';
import { Icon } from '../concepts/resources/Icon';
import { Pill } from './Pill';

const XAutosizeInput = AutoSizeInput as any;

export const PillableItem = (id: string, label: string, icon?: string) => ({ id, label, icon });
export interface PillableItem {
    readonly id: string;
    readonly label: string;
    readonly icon?: string;
}

type ItemCallback<T extends PillableItem> = (item: T) => void;
type ItemEditor<T extends PillableItem> = React.ComponentType<{ item: T, onEdit?: ItemCallback<T> }>;

export interface PillEditorConfig<T extends PillableItem> {
    elementLabel?: string;
    elementMenu?: (props: PillEditorProps<T>) => JSX.Element;
    elementEditor?: ItemEditor<T>;
    canEdit?: boolean;
    canRemove?: boolean;
    canAdd?: boolean;
    canSort?: boolean;
}

export interface PillEditorProps<T extends PillableItem = PillableItem> {
    items: T[];
    onUpdate: VoidCallback1<T[]>;
    config?: PillEditorConfig<T>;
}

const pillEditorConfigDefaults = {
    elementLabel: '',
    canEdit: true,
    canRemove: true,
    canAdd: true,
    canSort: true,
};

const ItemHandle = styled.div`
    margin: 0 .25rem 0 -.75rem;
    width: 1.5rem;
    color: ${Colors.gray300};    
    user-select: none;
    cursor: move;
`;

const ItemContainer = styled.div`
    position: relative;
`;

const ItemDropIndicator = styled.div`
    display: inline-block;
    width: 0.1875rem;
    height: 1.5rem;
    margin: 0.75rem 0;
    vertical-align: top;
    background: #2141ab;
    border-radius: 1000px;
`;

const ItemDroppableFrame = styled.div`
    display: inline-block;
    position: relative;
    vertical-align: top;
`;

const beforeOrAfter = (list: PillableItem[], fromId: string, toId: string) => {
    const from = list.findIndex(x => x.id === fromId);
    const to = list.findIndex(x => x.id === toId);
    return from < to ? 1 : -1;
};

const ItemDroppable: LFC<{ list: PillableItem[], item: PillableItem }> = ({ list, item, children }) => {

    const all = useDroppable({
        id: item.id,
    });

    const { isOver, setNodeRef, active } = all;
    const isOverSelf = active?.id === item.id;
    const boa = isOver && !isOverSelf ? beforeOrAfter(list, active.id, item.id) : 0;

    return <ItemDroppableFrame ref={setNodeRef}>
        {boa < 0 && <ItemDropIndicator />}
        {children}
        {boa > 0 && <ItemDropIndicator />}
    </ItemDroppableFrame>;
};

const ItemDraggableFrame = styled.div`
    display: inline-block;
    position: relative;
`;

const ItemDraggable: LFC<{ item: PillableItem }> = ({ item, children }) => {

    const { attributes, listeners, setNodeRef, isDragging, transform } = useDraggable({
        id: item.id,
        data: { id: item.id },
    });

    const style: any = {
        pointerEvents: isDragging ? 'none' : '',
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : 'none',
        zIndex: isDragging ? 100 : undefined,
    };

    return <ItemDraggableFrame ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
    </ItemDraggableFrame>;
};

type ItemProps<T extends PillableItem> = { list: T[], item: T, editor?: ItemEditor<T>, onEdit?: ItemCallback<T>, onRemove?: ItemCallback<T>, canSort?: boolean };

const Item = <T extends PillableItem>({ list, item, editor: Editor, onEdit, onRemove, canSort }: ItemProps<T>) => {

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-start',
    });

    const [editing, setEditing] = useState(false);
    const [label, setLabel] = useState(item.label);
    const hasLabelChanged = label === useMemo(() => item.label, [item]);

    const onStartEditing = useCallback(() => {
        if (!editing && !!onEdit) setEditing(true);
    }, [editing, onEdit]);

    const onFinishEditing = useCallback((confirm: boolean) => {
        if (confirm) onEdit({ ...item, ...(hasLabelChanged ? { label } : {}) });
        else setLabel(item.label);
        setEditing(false);
    }, [hasLabelChanged, label, item, onEdit]);

    const onItemClick = useCallback((e: any) => {
        if (e.button === 0) onStartEditing();
        else if (e.button === 1) onRemove(item);
    }, [onStartEditing, onRemove, item]);

    const pn = promptName();

    return <ItemDroppable list={list} item={item}>
        <ItemDraggable item={item}>
            <Pill ref={setRefElmt} canEdit={!!onEdit} editing={editing} className={`ignore-${pn}`} onMouseUp={onItemClick} >
                {canSort && <ItemHandle>
                    <Icon size="1.5" src="deqo://icons/hero/dots-vertical" />
                </ItemHandle>}
                <Pill.Inner>
                    {item.icon && <Icon size="1" src={item.icon} />}
                    {editing && (
                        <Prompt name={pn} clickOffIsPositive onAction={onFinishEditing}>
                            <XAutosizeInput autoFocus type="text" autoComplete="false" value={label} maxLength={32}
                                onChange={(e: any) => setLabel(e.target.value)} onFocus={(e: any) => e.target.select()} />
                        </Prompt>
                    )}
                    {!editing && item.label}
                    {onRemove && <Pill.Close onClick={() => onRemove(item)} />}
                </Pill.Inner>
            </Pill>

        </ItemDraggable>
        {editing && Editor && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} clickOffIsPositive ignoreEnter ignoreEscape onAction={onFinishEditing}>
                <PopperInner direction="down" className={`ignore-${pn}`}>
                    <Float>
                        <Editor item={item} onEdit={onEdit} />
                    </Float>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </ItemDroppable>;
};

const DragDropContainer: LFC<PillEditorProps> = ({ items, onUpdate, config, children }) => {

    const onDragEnd = useCallback((event: DragEndEvent) => {
        if (!event.over) return;
        const from = items.findIndex(x => x.id === event.active.id);
        const to = items.findIndex(x => x.id === event.over.id);
        const boa = from < to ? 1 : -1;
        const item = items[from];
        items = items.slice();
        items.splice(from, 1);
        items.splice(to, 0, item);
        onUpdate(items);
    }, [items, onUpdate]);

    const activationConstraint = { distance: 5 };
    const mouseSensor = useSensor(MouseSensor, { activationConstraint });
    const touchSensor = useSensor(TouchSensor, { activationConstraint });
    const sensors = useSensors(mouseSensor, touchSensor);

    if (config.canSort) {
        return <DndContext sensors={sensors} onDragEnd={onDragEnd}>
            {children}
        </DndContext>;
    }
    else {
        return <>{children}</>;
    }
};

const ButtonFrame = styled.div`
    display: inline-flex;
    height: 2.5rem;
    margin: .25rem;
    align-items: center;
    justify-content: center;
    vertical-align: bottom;
`;

const AddButton: LFC<PillEditorProps> = props => {

    const { elementMenu: Content } = props.config;

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-start',
    });

    const [open, setOpen] = useState(false);
    const onToggle = () => setOpen(x => !x);

    const onUpdate = useCallback((items: PillableItem[]) => {
        setOpen(false);
        props.onUpdate(items);
    }, [props.onUpdate]);

    const pn = promptName();

    return <>
        <ButtonFrame ref={setRefElmt} className={`ignore-${pn}`}>
            <Button disabled={!Content} variant="primary" size="xs" label="Add..." onAction={onToggle} />
        </ButtonFrame>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => setOpen(false)}>
                <PopperInner direction="down">
                    <Float>
                        {Content && <Content {...props} onUpdate={onUpdate} />}
                    </Float>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </>;
};

export const PillEditor = <T extends PillableItem = PillableItem>({ config, items, onUpdate }: React.PropsWithChildren<PillEditorProps<T>>) => {
    config = { ...pillEditorConfigDefaults, ...(config ?? {}) };

    const onEdit = useCallback((item: T) => {
        onUpdate(items.map(x => x.id === item.id ? item : x));
    }, [items, onUpdate]);

    const onRemove = useCallback((item: T) => {
        onUpdate(items.filter(x => x.id !== item.id));
    }, [items, onUpdate]);

    const passThru = { config, items, onUpdate } as any;

    return <DragDropContainer {...passThru}>
        <ItemContainer>
            {items.map(tm =>
                <Item key={tm.id}
                    list={items}
                    item={tm}
                    editor={config.elementEditor}
                    onEdit={config.canEdit ? onEdit : null}
                    onRemove={config.canRemove ? onRemove : null}
                    canSort={config.canSort}
                />
            )}
            {config.canAdd && <AddButton {...passThru} />}
        </ItemContainer>
    </DragDropContainer>;

};
